/**
 * Automatic slow-motion near flybys (Sprint 2, S2-11 / D10). Playback speed is capped so that the remaining mission
 * time to the nearest closest approach takes at least `leadS` seconds of real time, but never below `floor`:
 *
 *   cap(Δt) = max(floor, |Δt| / leadS)
 *
 * Approaching at any speed, time to the flyby then shrinks exponentially (time constant leadS) — a smooth brake that
 * needs no look-ahead — until the floor speed (6 h/s) carries playback through closest approach; afterwards the cap
 * opens again, three times faster (SLOWMO_EXIT_S): at 1 year/s a symmetric brake kept the badge on for months of
 * mission time after a flyby (maintainer feedback, Sprint 3). At 1 month/s the Jupiter flyby takes about 6 s instead
 * of 0.13 s.
 */

/** Floor speed inside the slow zone: 6 hours of mission time per second (spike Q6). */
export const SLOWMO_FLOOR = 6 * 3600;
/** Seconds of real time the approach to a flyby is stretched to. */
export const SLOWMO_LEAD_S = 1.5;
/** After closest approach: seconds of real time the departure is stretched to. */
export const SLOWMO_EXIT_S = 0.5;

/**
 * Speed cap (mission seconds per real second) at `nowMs` for the flyby times `flybysMs` (sorted or not).
 * Infinity when there are no flybys.
 */
export function slowmoCap(
  nowMs: number,
  flybysMs: readonly number[],
  floor = SLOWMO_FLOOR,
  leadS = SLOWMO_LEAD_S,
  exitS = SLOWMO_EXIT_S,
): number {
  let cap = Infinity;
  for (const t of flybysMs) cap = Math.min(cap, Math.abs(nowMs - t) / 1000 / (nowMs < t ? leadS : exitS));
  return Number.isFinite(cap) ? Math.max(floor, cap) : Infinity;
}

/** The flyby (ms) nearest to `nowMs`, or NaN. */
export function nearestFlyby(nowMs: number, flybysMs: readonly number[]): number {
  let best = Number.NaN;
  for (const t of flybysMs) if (Number.isNaN(best) || Math.abs(nowMs - t) < Math.abs(nowMs - best)) best = t;
  return best;
}
